import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { CalendarIcon, MapPinIcon } from "../components/icons.jsx";
import { API } from "../config/api.js";
import { get, post } from "../lib/http.js";
import { format } from "date-fns";
import FancyLoader from "../components/FancyLoader.jsx";
import { toast } from "react-toastify";
import { readBbUserContact, toTenDigitMobile } from "../utils/phone.js";
import { fetchUserProfile } from "../lib/userProfile.js";
import { openRazorpayCheckout } from "../lib/razorpayCheckout.js";
import { formatDayLabel, ticketDisplayName, ticketDateFromType } from "../listYourShow/scheduleHelpers.js";
import {
  coupleQuantityLabel,
  coupleTicketBadge,
  isAllDaysTicket,
  isCoupleTicket,
  isGroupTicket,
  isTicketSaleActive,
  personsPerUnit,
  regularTicketName,
  suggestCoupleTicketName,
  ticketCategoryId,
  ticketSelectionKey,
  ticketTierLabel,
  totalPersons,
} from "../lib/ticketCouple.js";
import { absMediaUrl, formatEventScheduleLabel } from "../api/events.js";

const formatINR = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";

// ✅ IST helper
const toISTDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  const fixed = /Z$/i.test(s) ? s.replace(/Z$/i, "+05:30") : s;
  return new Date(fixed);
};

// ✅ Strict Exhibition check
const isExhibitionStrict = (ev) => String(ev?.category || "") === "Exhibition";

// ✅ Simple validators
const isNonEmpty = (s) => String(s || "").trim().length > 0;
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
const isPhone = (s) => /^[0-9]{10,15}$/.test(String(s || "").trim().replace(/\D/g, ""));

export default function CheckoutPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [search] = useSearchParams();

  // Core states
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [payoutLoading, setPayoutLoading] = useState(false);

  // small fix: user loading state referenced previously
  const [userLoading, setUserLoading] = useState(false);

  // Coupons
  const [discounts, setDiscounts] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Coupon popup state (new)
  const [showCouponPopup, setShowCouponPopup] = useState(false);
  const [couponPopupInfo, setCouponPopupInfo] = useState({
    code: "",
    discountAmount: 0,
    finalAmount: 0,
    cartAmount: 0,
  });

  // T&C modal state
  const [tncOpen, setTncOpen] = useState(false);
  const [tncContent, setTncContent] = useState("");
  const [tncTitle, setTncTitle] = useState("Terms & Conditions");

  // View-all toggle
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const [showPopupSuccess, setShowPopupSuccess] = useState(false);
  const [popupBooking, setPopupBooking] = useState(null);

  const debounceRef = useRef(null);
  /** One Razorpay link per cart snapshot (tier / qty / coupon / server total). */
  const payoutIdempotencyRef = useRef({ sig: "", key: "" });

  // ✅ Attendees for Exhibition tickets only (includes companyName & designation)
  const [attendees, setAttendees] = useState([
    { name: "", email: "", phone: "", companyName: "", designation: "" },
  ]);

  // Derived Exhibition flag (strict)
  const exhibition = isExhibitionStrict(event);

  const perOrderCap = exhibition ? 2 : 6;
  const maxQtyForTier = useMemo(() => {
    if (!selectedTier) return 0;
    const seats = Number(selectedTier.availableSeats ?? perOrderCap);
    return Math.max(1, Math.min(perOrderCap, seats > 0 ? seats : perOrderCap));
  }, [selectedTier, perOrderCap]);

  useEffect(() => {
    if (maxQtyForTier <= 0) return;
    setQty((q) => Math.min(Math.max(1, q), maxQtyForTier));
  }, [maxQtyForTier]);

  const [details, setDetails] = useState(() => {
    const c = readBbUserContact();
    return { name: c.name, email: c.email, mobile: c.mobile };
  });
  const [detailsSubmitting, setDetailsSubmitting] = useState(false);

  // ---------------------
  // Important: auto-close effect must be declared BEFORE any early return so hooks order stays stable
  useEffect(() => {
    if (!showCouponPopup) return;
    const t = setTimeout(() => {
      setShowCouponPopup(false);
    }, 3500);
    return () => clearTimeout(t);
  }, [showCouponPopup]);
  // ---------------------

  useEffect(() => {
    (async () => {
      const session = readBbUserContact();
      setDetails((d) => ({
        name: d.name || session.name,
        email: d.email || session.email,
        mobile: d.mobile || session.mobile,
      }));

      try {
        setUserLoading(true);
        if (!session.token || !session.userId) return;
        const res = await fetchUserProfile(session.userId, session.token);
        if (!res) return;
        const mobile = toTenDigitMobile(res.mobile || res.phone) || session.mobile;
        setDetails({
          name: res?.name || session.name || "",
          email: res?.email || session.email || "",
          mobile,
        });
      } catch {
        if (!session.mobile) {
          toast.error("Failed to load profile.");
        }
      } finally {
        setUserLoading(false);
      }
    })();
  }, []);

  // Keep attendees length in sync with qty — only for Exhibition
  useEffect(() => {
    if (!exhibition) return;
    setAttendees((prev) => {
      const next = [...prev];
      if (qty > next.length) {
        return next.concat(
          Array.from({ length: qty - next.length }, () => ({
            name: "",
            email: "",
            phone: "",
            companyName: "",
            designation: "",
          }))
        );
      } else if (qty < next.length) {
        return next.slice(0, qty);
      }
      return next;
    });
  }, [qty, exhibition]);

  // All attendees valid? — Exhibition requires ALL fields
  const allAttendeesValid = useMemo(() => {
    if (!exhibition) return true;
    if (!Array.isArray(attendees) || attendees.length !== qty) return false;
    return attendees.every(
      (a) =>
        isNonEmpty(a.name) &&
        isEmail(a.email) &&
        isPhone(a.phone) &&
        isNonEmpty(a.companyName) &&
        isNonEmpty(a.designation)
    );
  }, [attendees, qty, exhibition]);

  const hardcodedTnc = `
🔓 How to Use:
Tap “Copy Code”
Go to Showtime Bro
Select your Ticket
Apply the code at checkout
Enjoy your discount!

📌 Terms:
Valid only on Showtime Bro
One-time use only
Coupon valid for a limited period only
Showtime Bro reserves the right to modify or cancel the offer without prior notice

`;

  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "InitiateCheckout");
    }
  }, []);

  // Price details from server (safe defaults)
  const [priceDetails, setPriceDetails] = useState({
    qty: 0,
    ticketPrice: 0,
    cartAmount: 0,
    convenienceFee: 0,
    gstRate: 0.18,
    gstAmount: 0,
    finalAmount: 0,
    discountApplied: false,
    discountAmount: 0,
    adjustedTicketCategories: [],
  });

  // ✅ Seating layout modal state
  const [layoutOpen, setLayoutOpen] = useState(false);

  // ✅ Checkout hero: prefer landscape banner, fall back to portrait
  const checkoutBannerSrc = useMemo(() => {
    if (!event) return "";
    const landscape = Array.isArray(event?.landsacpImage)
      ? event.landsacpImage.find(Boolean)
      : event?.landsacpImage;
    return (
      absMediaUrl(landscape) ||
      absMediaUrl(event?.imageUrl) ||
      absMediaUrl(event?.image) ||
      ""
    );
  }, [event]);

  // ✅ Resolve a layout image
  const layoutSrc = useMemo(() => {
    if (!event) return "";
    const candidates = [
      event?.layoutImage,
      event?.layoutUrl,
      event?.venue?.layoutImage,
      event?.venue?.layoutUrl,
      Array.isArray(event?.landsacpImage)
        ? event.landsacpImage.find(
          (u) => typeof u === "string" && /layout|seat|map|plan/i.test(u)
        ) || event.landsacpImage[0]
        : null,
    ].filter(Boolean);
    return candidates[0] || "";
  }, [event]);

  /* qty change par coupon reset + reprice */
  function clearCouponAndReprice(newQty) {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    if (selectedTier) updatePriceDetails(selectedTier, newQty, undefined);
  }

  /* Load coupons for this event */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(`${API.coupon.getDiscount}?eventId=${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!alive) return;
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.discounts)
            ? res.discounts
            : Array.isArray(res?.discount)
              ? res.discount
              : Array.isArray(res?.data?.discounts)
                ? res.data.discounts
                : [];
        setDiscounts(list);
      } catch (e) {
        console.error("load discounts error:", e);
        setDiscounts([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  /* Load event & tickets */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!alive) return;

        const ev = res?.event;
        setEvent(ev || null);

        const qDate = String(search.get("date") || "").trim();
        const qPass = String(search.get("pass") || "").trim().toLowerCase();
        const wantSeason = qPass === "season" || qPass === "all" || qDate === "all";

        const mapped = (ev?.ticketCategories || [])
          .filter((t) => isTicketSaleActive(t))
          .map((t, i) => {
          const categoryId = ticketCategoryId(t) || String(i);
          const couple = isCoupleTicket(t);
          const group = isGroupTicket(t);
          const ppu = personsPerUnit(t);
          const recoveredDate =
            String(t?.date || "").trim() || ticketDateFromType(t?.type || "") || "";
          const season = !recoveredDate && isAllDaysTicket(t, ev);
          let desc = t.desc || "Ticket";
          if (group) desc = `Group ticket — valid for ${ppu} persons per pack`;
          else if (couple) desc = "Couple ticket — valid for 2 persons per ticket";
          if (season) desc = `${desc} · Season pass (all dates)`;
          if (t.phaseLabel === "early_bird") desc = `Early Bird · ${desc}`;
          return {
            key: ticketSelectionKey({ ...t, categoryId }, i),
            categoryId,
            tier: ticketTierLabel({ ...t, categoryId }, ticketDisplayName) || `Tier ${i + 1}`,
            date: season ? "" : recoveredDate,
            validFor: season ? "all_days" : "one_day",
            desc,
            isCoupleTicket: couple,
            isGroupTicket: group,
            ticketKind: t.ticketKind || (group ? "group" : couple ? "couple" : "single"),
            personsPerUnit: ppu,
            phaseLabel: t.phaseLabel || "",
            perks: t.perks,
            price: Number(t.price || 0),
            fee: Number(t.fee || 0),
            isTicketAvailable: t.isTicketAvailable,
            availableSeats: Number(t.availableSeats ?? 0),
            tone:
              Number(t.availableSeats ?? 0) === 0
                ? "red"
                : Number(t.availableSeats ?? 0) < 20
                  ? "amber"
                  : "green",
            badge:
              Number(t.availableSeats ?? 0) === 0
                ? "Sold Out"
                : Number(t.availableSeats ?? 0) < 20
                  ? `Only ${Number(t.availableSeats)} left`
                  : "Available",
          };
        });

        // Strict filter by booking choice:
        // - pass=season → only all-days season tickets
        // - date=YYYY-MM-DD → only that day's tickets (no season mix)
        // - neither → show all (legacy / direct link)
        let list = mapped;
        if (wantSeason) {
          list = mapped.filter((t) => t.validFor === "all_days");
        } else if (qDate) {
          list = mapped.filter(
            (t) => t.validFor !== "all_days" && t.date === qDate
          );
        }
        setTickets(list);

        // Preselect from query ?tier= (within selected date pool)
        const qKey = search.get("tier");
        let pre = null;
        if (qKey) {
          pre =
            list.find((t) => t.categoryId === qKey) ||
            list.find((t) => t.key === qKey) ||
            list.find((t) => t.tier === qKey);
        }
        if (!pre) pre = list.find((t) => t.availableSeats > 0) || list[0] || null;
        setSelectedTier(pre || null);

        const qQty = Number(search.get("qty") || 0);
        if (Number.isFinite(qQty) && qQty >= 1) {
          const seats = Number(pre?.availableSeats ?? 0);
          const cap = Number(ev?.maxTicketsPerUser) || 10;
          const maxQ = Math.max(1, Math.min(cap, seats || cap));
          setQty(Math.min(Math.floor(qQty), maxQ));
        }
      } catch (e) {
        console.error("checkout fetch event error:", e);
        setEvent(null);
        setTickets([]);
        setSelectedTier(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, search]);

  /* Server pricing refresh (selectedTier/qty/coupon) */
  useEffect(() => {
    let alive = true;
    if (!selectedTier) return;

    (async () => {
      try {
        const tierId = selectedTier.categoryId || selectedTier.key;
        const expanded = Array.from({ length: qty }, () => ({
          categoryId: tierId,
          quantity: 1,
          isCoupleTicket: !!selectedTier.isCoupleTicket,
          isGroupTicket: !!selectedTier.isGroupTicket,
          ticketKind: selectedTier.ticketKind,
          personsPerUnit: personsPerUnit(selectedTier),
        }));

        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const userId = JSON.parse(localStorage.getItem("bb_user"))?.id;

        const payload = {
          userId,
          eventId: id,
          ticketCategories: expanded,
          ...(appliedCoupon?.code ? { discountCode: appliedCoupon.code } : {}),
        };

        const res = await post(`${API.coupon.validDescount}`, payload, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });

        if (!alive) return;
        const pricePayload = res?.data?.priceDetails || res?.data || res;
        if (pricePayload) setPriceDetails((p) => ({ ...p, ...pricePayload }));
      } catch (e) {
        if (e?.code === "MAX_TICKETS_PER_USER") {
          toast.warn(e.message);
        }
        console.error("load pricing error:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, selectedTier, qty, appliedCoupon?.code]);

  /* Coupon helpers */
  const now = Date.now();
  const isCouponApplicableToEvent = (c) => {
    if (!Array.isArray(c.applicableEvents) || c.applicableEvents.length === 0) return true;
    return c.applicableEvents.includes(event?._id);
  };
  const isWithinValidity = (c) => {
    const vf = c.validFrom ? new Date(c.validFrom).getTime() : null;
    const vt = c.validTo ? new Date(c.validTo).getTime() : null;
    if (vf && now < vf) return false;
    if (vt && now > vt) return false;
    return true;
  };
  const hasUsageLeft = (c) => {
    if (!c.usageLimit || c.usageLimit <= 0) return true;
    const used = Number(c.usedCount ?? 0);
    return used < c.usageLimit;
  };
  const meetsMinPurchase = (c) => {
    const min = Number(c.minPurchaseAmount ?? 0);
    return priceDetails.cartAmount >= min;
  };

  // Input code -> matching discount (for T&C if needed later)
  const matchedDiscountForInput = useMemo(() => {
    const code = String(couponCode || "").trim().toLowerCase();
    if (!code) return null;
    return discounts.find((d) => String(d.code || "").toLowerCase() === code) || null;
  }, [couponCode, discounts]);

  // Open T&C (coupon or event)
  function openTncFor(coupon, titleSuffix = "") {
    const raw = coupon?.termsAndConditions ?? event?.termsAndConditions ?? "";
    let content = "";
    let items = null;

    if (Array.isArray(raw)) {
      items = raw.map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean);
    } else if (typeof raw === "string") {
      const trimmed = raw.trim();
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          items = parsed.map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean);
        } else {
          content = trimmed;
        }
      } catch {
        if (/<[^>]+>/.test(trimmed)) {
          content = trimmed;
        } else {
          const split = trimmed
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          items = split.length ? split : null;
          if (!items) content = trimmed;
        }
      }
    }

    setTncContent(items?.length ? items : content || "No terms & conditions available.");
    setTncTitle(`Terms & Conditions${titleSuffix ? ` — ${titleSuffix}` : ""}`);
    setTncOpen(true);
  }

  // Server pricing (single source of truth)
  async function updatePriceDetails(tier, quantity, appliedCode, opts = {}) {
    const { surfaceErrors = false } = opts;
    if (!tier) {
      if (surfaceErrors) setCouponError("Please select a ticket category first.");
      return false;
    }

    try {
      const user = JSON.parse(localStorage.getItem("bb_user") || "null");
      const token = user?.token;
      const userId = user?.id;
      const codeTrimmed = String(appliedCode ?? "").trim();

      const payload = {
        userId,
        eventId: id,
        ticketCategories: [{
          categoryId: tier.categoryId || tier.key,
          quantity,
          isCoupleTicket: !!tier.isCoupleTicket,
          isGroupTicket: !!tier.isGroupTicket,
          ticketKind: tier.ticketKind,
          personsPerUnit: personsPerUnit(tier),
        }],
        ...(codeTrimmed ? { discountCode: codeTrimmed } : {}),
      };

      const res = await post(`${API.coupon.validDescount}`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      const success = res?.success ?? res?.ok ?? true;
      const message =
        res?.message || res?.error || res?.data?.message || res?.data?.error || "";

      if (!success) {
        if (surfaceErrors) setCouponError(message || "Failed to apply coupon.");
        if (res?.code === "MAX_TICKETS_PER_USER") toast.warn(message);
        return false;
      }

      const pricePayload = res?.priceDetails || res?.data?.priceDetails || res?.data || res;
      if (pricePayload) {
        setPriceDetails((p) => ({ ...p, ...pricePayload }));
        // Return the payload so caller can show popup info
        return pricePayload;
      }

      // If no payload, still return a generic true
      return true;
    } catch (e) {
      console.error("updatePriceDetails error:", e);
      const serverMsg = e?.message || "Failed to apply coupon.";
      if (e?.code === "MAX_TICKETS_PER_USER") {
        toast.warn(serverMsg);
      }
      if (surfaceErrors) setCouponError(serverMsg);
      return false;
    }
  }

  async function handleSelectTier(t) {
    try {
      setSelectedTier(t);
      setQty(1);
      setAppliedCoupon(null);
      setCouponCode("");
      setCouponError("");

      const user = JSON.parse(localStorage.getItem("bb_user") || "null");
      const token = user?.token;

      const payload = {
        userId: user?.id,
        eventId: id,
        ticketCategories: [{
          categoryId: t.categoryId || t.key,
          quantity: 1,
          isCoupleTicket: !!t.isCoupleTicket,
          isGroupTicket: !!t.isGroupTicket,
          ticketKind: t.ticketKind,
          personsPerUnit: personsPerUnit(t),
        }],
      };

      const res = await post(`${API.coupon.validDescount}`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      const pricePayload = res?.data?.priceDetails || res?.data || res;
      if (pricePayload) setPriceDetails((p) => ({ ...p, ...pricePayload }));
    } catch (e) {
      console.error("load discounts error:", e);
    }
  }

  // applyByCode (updated to show popup)
  const applyByCode = async (code) => {
    setCouponError("");
    const input = String(code ?? "").trim();
    if (!input) {
      setAppliedCoupon(null);
      setCouponCode("");
      await updatePriceDetails(selectedTier, qty, "", { surfaceErrors: false });
      return;
    }

    if (applyingCoupon) return;

    setApplyingCoupon(true);
    try {
      const pricePayload = await updatePriceDetails(selectedTier, qty, input, {
        surfaceErrors: true,
      });

      // pricePayload will be false on failure, or the payload object on success
      if (pricePayload) {
        const discountAmount =
          Number(pricePayload.discountAmount ?? pricePayload.discount ?? 0) || 0;
        const discountApplied = Boolean(pricePayload.discountApplied);
        const hasFree =
          Array.isArray(pricePayload.adjustedTicketCategories) &&
          pricePayload.adjustedTicketCategories.some(
            (t) => Number(t?.freeQuantity || 0) > 0
          );

        // Coupon accepted by API but not actually usable on this ticket
        if (!discountApplied && discountAmount <= 0 && !hasFree) {
          setAppliedCoupon(null);
          setCouponError("This coupon is not valid for this ticket");
          await updatePriceDetails(selectedTier, qty, "", { surfaceErrors: false });
          return;
        }

        setAppliedCoupon({ code: input });
        setCouponCode(input);
        setCouponError("");

        // prepare popup info - defensive fields
        const finalAmount =
          Number(pricePayload.finalAmount ?? pricePayload.total ?? 0) || 0;
        const cartAmount =
          Number(pricePayload.cartAmount ?? pricePayload.subtotal ?? 0) || 0;

        setCouponPopupInfo({
          code: input.toUpperCase(),
          discountAmount,
          finalAmount,
          cartAmount,
        });

        // Show animated popup
        setShowCouponPopup(true);
      } else {
        setAppliedCoupon(null);
      }
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    setShowCouponPopup(false);
    setCouponPopupInfo({ code: "", discountAmount: 0, finalAmount: 0, cartAmount: 0 });
    if (selectedTier) {
      await updatePriceDetails(selectedTier, qty, undefined);
    }
  };

  const eligibleCoupons = useMemo(() => {
    return (discounts || []).filter(
      (c) =>
        c?.isActive !== false &&
        isCouponApplicableToEvent(c) &&
        isWithinValidity(c) &&
        hasUsageLeft(c)
    );
  }, [discounts, event?._id, now]);

  const displayCoupons = useMemo(() => eligibleCoupons, [eligibleCoupons]);

  // ORDER & SLICE COUPONS
  const orderedCoupons = useMemo(() => {
    const base = Array.isArray(displayCoupons) ? [...displayCoupons] : [];
    if (appliedCoupon?.code) {
      const idx = base.findIndex((c) => c.code === appliedCoupon.code);
      if (idx > -1) {
        const [sel] = base.splice(idx, 1);
        base.unshift(sel);
      }
    }
    return base;
  }, [displayCoupons, appliedCoupon?.code]);

  const SHOW_COUNT = 1;
  const couponsToRender = useMemo(
    () => (showAllCoupons ? orderedCoupons : orderedCoupons.slice(0, SHOW_COUNT)),
    [orderedCoupons, showAllCoupons]
  );

  const reasonNotEligible = (c) => {
    if (!isCouponApplicableToEvent(c)) return "Not for this event";
    if (!isWithinValidity(c)) return "Not in validity period";
    if (!hasUsageLeft(c)) return "Usage limit reached";
    if (!meetsMinPurchase(c)) return `Min. purchase ₹${Number(c.minPurchaseAmount || 0)}`;
    return "";
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  /* -------------------- Misc helpers -------------------- */
  if (!event || !selectedTier)
    return (
      <main className="w-full px-4 py-10 text-center justify-center">
        <FancyLoader label="Processing..." />
      </main>
    );

  // header
  const headerLocation = event?.venue
    ? `${event.venue.name || ""} ${event.venue.city || ""}`.trim()
    : "—";
  const selectedDateParam = String(search.get("date") || "").trim();
  const selectedPassParam = String(search.get("pass") || "").trim().toLowerCase();
  const isSeasonCheckout =
    selectedPassParam === "season" ||
    selectedPassParam === "all" ||
    selectedDateParam === "all";
  const headerDate = isSeasonCheckout
    ? "Season pass · All dates"
    : selectedDateParam
    ? (() => {
        const d = new Date(`${selectedDateParam}T12:00:00`);
        if (Number.isNaN(d.getTime())) return formatDayLabel(selectedDateParam);
        return d.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })()
    : formatEventScheduleLabel(event) || "—";

  // ✅ Exhibition: allow 1 or 2; Others: 1–6
  const minQtyRequired = 1;

  // ✅ payout payload
  const buildPayoutBody = () => {
    const user = JSON.parse(localStorage.getItem("bb_user")) || {};
    const userId =
      user?.user?._id || user?.user?.id || user?._id || user?.id || "";

    const tierKey = selectedTier?.categoryId || selectedTier?.key || "";
    const couponPart = appliedCoupon?.code || "";
    const totalRounded = Math.round(Number(priceDetails?.finalAmount ?? 0));
    const idemSig = `${String(event?._id || id || "")}|${tierKey}|${qty}|${couponPart}|${totalRounded}`;
    if (payoutIdempotencyRef.current.sig !== idemSig) {
      payoutIdempotencyRef.current = {
        sig: idemSig,
        key:
          typeof globalThis.crypto?.randomUUID === "function"
            ? globalThis.crypto.randomUUID()
            : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`,
      };
    }

    const base = {
      amount: Math.round(priceDetails.finalAmount),
      userId,
      eventId: event?._id,
      ticketCategories: [
        {
          categoryId: selectedTier?.categoryId || selectedTier?.key,
          quantity: qty,
          price: Number(selectedTier?.price || 0),
          isCoupleTicket: !!selectedTier?.isCoupleTicket,
          isGroupTicket: !!selectedTier?.isGroupTicket,
          ticketKind: selectedTier?.ticketKind,
          personsPerUnit: personsPerUnit(selectedTier),
        },
      ],
      discountCode: appliedCoupon?.code || "",
      idempotencyKey: payoutIdempotencyRef.current.key,
    };

    // Only for Exhibition: send guestDetails (exact key as requested)
    if (exhibition) {
      base.gestDetails = attendees.map((a, i) => ({
        index: i + 1,
        name: String(a.name || "").trim(),
        email: String(a.email || "").trim(),
        phone: String(a.phone || "").trim(),
        companyName: String(a.companyName || "").trim(),
        designation: String(a.designation || "").trim(),
      }));
    }

    return base;
  };

  async function handlePayoutClick(userDetails) {
    try {
      if (!selectedTier) {
        alert("Please select a ticket category.");
        return;
      }
      if (exhibition && !allAttendeesValid) {
        toast.error("Please fill Name, Email, Phone, Company Name & Designation for all attendees.");
        return;
      }

      const session = readBbUserContact();
      const mobile =
        toTenDigitMobile(userDetails?.mobile) || session.mobile;
      if (!mobile) {
        toast.error("Mobile number missing. Please log in again with OTP.");
        return;
      }

      setPayoutLoading(true);
      let body = buildPayoutBody ? buildPayoutBody() : {};
      body.userDetails = {
        name: (userDetails?.name || session.name || "").trim(),
        email: (userDetails?.email || session.email || "").trim(),
        mobile,
      };
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await post(API.payout.payout, body, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      if (res?.success && res?.checkout?.order_id) {
        toast.success("Opening secure payment…");

        const checkoutPayload = {
          ...res.checkout,
          prefill: {
            ...res.checkout.prefill,
            contact:
              res.checkout.prefill?.contact ||
              mobile ||
              session.mobile,
          },
        };

        await openRazorpayCheckout({
          checkout: checkoutPayload,
          onSuccess: async (paymentId) => {
            await post(
              API.payout.PaymentSuccessful,
              { razorpay_payment_id: paymentId },
              {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                  "Content-Type": "application/json",
                },
              }
            );
            nav(
              `/payment-successful?razorpay_payment_id=${encodeURIComponent(paymentId)}`
            );
          },
          onDismiss: () => {
            toast.info("Payment cancelled");
          },
        });
      } else if (res?.success && res?.payment_url) {
        window.location.href = res.payment_url;
      } else if (res?.success) {
        setPopupBooking(res?.booking || {});
        setShowPopupSuccess(true);
      } else {
        toast.error(res?.message || "Failed to create payment link");
      }
    } catch (err) {
      console.error("Payout API error:", err?.message || err);
      toast.error(err?.message || "Failed to initiate payout. Please try again.");
    } finally {
      setPayoutLoading(false);
    }
  }

  const handleClose = () => {
    setShowPopupSuccess(false);
    nav("/tickets");
  };

  const submitDetailsAndPay = async () => {
    if (!details.name?.trim()) {
      toast.error("Please enter name");
      return;
    }
    if (!isEmail(details.email)) {
      toast.error("Please enter a valid email");
      return;
    }
    if (!toTenDigitMobile(details.mobile)) {
      toast.error("Mobile number missing. Please log in again.");
      return;
    }
    setDetailsSubmitting(true);
    try {
      await handlePayoutClick(details);
    } finally {
      setDetailsSubmitting(false);
    }
  };

  const detailsEmailValid = isEmail(details.email);

  // ✅ disable logic for button (Exhibition rules + required email)
  const bookDisabled =
    payoutLoading ||
    detailsSubmitting ||
    !selectedTier ||
    maxQtyForTier <= 0 ||
    qty < minQtyRequired ||
    qty > maxQtyForTier ||
    !details.name?.trim() ||
    !detailsEmailValid ||
    (exhibition && !allAttendeesValid);

  /* --------------------------------- UI --------------------------------- */
  return (
    <div
      className="stb-checkout min-h-[100dvh]"
      style={{ fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif' }}
    >
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,600;0,9..40,700;0,9..40,800&family=Oswald:wght@500;600;700&display=swap");
        .stb-checkout {
          background:
            radial-gradient(1100px 480px at 8% -8%, rgba(251,191,36,0.26), transparent 55%),
            radial-gradient(800px 380px at 100% 0%, rgba(249,115,22,0.14), transparent 48%),
            linear-gradient(180deg, #fff8e7 0%, #f4f4f5 45%, #eef2f7 100%);
        }
        .dark .stb-checkout {
          background:
            radial-gradient(900px 420px at 0% 0%, rgba(245,158,11,0.18), transparent 50%),
            linear-gradient(180deg, #0b0b0c 0%, #12141a 60%, #0f1115 100%);
        }
        .stb-co-display { font-family: "Oswald", ui-sans-serif, system-ui, sans-serif; }
        @keyframes stbCoIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .stb-co-anim { animation: stbCoIn 360ms cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>

      {/* Brand checkout header */}
      <section className="border-b border-amber-200/80 bg-[#fff8e1] dark:border-slate-700 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex min-w-0 items-center gap-3">
              {checkoutBannerSrc ? (
                <img
                  src={checkoutBannerSrc}
                  alt=""
                  className="h-16 w-24 shrink-0 rounded-xl border border-amber-200 bg-white object-cover md:h-20 md:w-32 dark:border-slate-700"
                  loading="eager"
                />
              ) : (
                <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-100 md:h-20 md:w-32 dark:border-slate-700 dark:bg-amber-950/40">
                  <span className="stb-co-display text-xs tracking-widest text-amber-700 dark:text-amber-400">STB</span>
                </div>
              )}
              <div className="min-w-0">
                <div className="stb-co-display text-[10px] uppercase tracking-[0.22em] text-amber-700 dark:text-amber-400">
                  Almost there · Checkout
                </div>
                <h1 className="stb-co-display mt-0.5 truncate text-xl font-semibold uppercase tracking-wide text-slate-900 dark:text-white md:text-2xl">
                  {event.name}
                </h1>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                    <MapPinIcon className="shrink-0" />
                    <span className="truncate">{headerLocation}</span>
                  </span>
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-amber-800 dark:text-amber-400">
                    <CalendarIcon className="shrink-0" />
                    <span className="truncate">{headerDate}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 md:ml-auto">
              <button
                type="button"
                onClick={() => nav(-1)}
                className="rounded-full border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-amber-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
              >
                ← Back
              </button>
              {layoutSrc && (
                <button
                  type="button"
                  onClick={() => setLayoutOpen(true)}
                  className="rounded-full border border-amber-300 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                  title="View seating layout"
                >
                  <span className="md:hidden">Layout</span>
                  <span className="hidden md:inline">Seating layout</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="stb-co-anim w-full pb-28 md:pb-10">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-6 grid gap-6 lg:grid-cols-3">
          {/* LEFT: confirm details + qty (+ attendees for Exhibition) */}
          <div className="order-2 space-y-6 lg:order-1 lg:col-span-2">
            <div className="lg:sticky lg:top-4 space-y-6">
              <section className="overflow-hidden rounded-2xl border border-amber-500/25 bg-white shadow-[0_18px_50px_-28px_rgba(0,0,0,0.35)] dark:border-amber-700/30 dark:bg-slate-900">
                <div className="border-b border-amber-500/20 bg-gradient-to-r from-amber-500/15 via-transparent to-orange-500/10 px-4 py-4 md:px-5">
                  <p className="stb-co-display text-[10px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">
                    Guest info
                  </p>
                  <h2 className="stb-co-display text-xl font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                    Confirm your details
                  </h2>
                  {isSeasonCheckout ? (
                    <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      Season pass · All event dates
                    </p>
                  ) : selectedDateParam ? (
                    <p className="mt-1 text-sm font-semibold text-amber-700 dark:text-amber-400">
                      Single day · {formatDayLabel(selectedDateParam)}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-4 p-4 md:p-5">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Name
                    <input
                      type="text"
                      value={details.name}
                      onChange={(e) =>
                        setDetails((s) => ({ ...s, name: e.target.value }))
                      }
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none ring-amber-400/0 transition focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-400/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:bg-slate-800"
                      placeholder="Full name"
                      autoComplete="name"
                    />
                  </label>

                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Email <span className="text-rose-500">*</span>
                    <input
                      type="email"
                      required
                      value={details.email}
                      onChange={(e) =>
                        setDetails((s) => ({ ...s, email: e.target.value }))
                      }
                      className={`mt-1.5 w-full rounded-xl border bg-slate-50 px-3 py-2.5 text-slate-900 outline-none transition focus:bg-white focus:ring-2 dark:bg-slate-800 dark:text-white ${
                        details.email && !detailsEmailValid
                          ? "border-rose-400 focus:ring-rose-300 dark:border-rose-500"
                          : "border-slate-200 focus:border-amber-400 focus:ring-amber-400/30 dark:border-slate-700"
                      }`}
                      placeholder="email@example.com"
                      autoComplete="email"
                    />
                    {!detailsEmailValid ? (
                      <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                        Email is required to continue
                      </p>
                    ) : null}
                  </label>

                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Mobile (cannot be changed)
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={details.mobile}
                      readOnly
                      disabled
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-slate-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400"
                    />
                  </label>

                  {/* Selected ticket summary + quantity */}
                  {selectedTier ? (
                    <div className="mt-1 flex items-center justify-between gap-4 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
                      <div className="min-w-0">
                        <div className="stb-co-display text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400">
                          Your pass
                        </div>
                        <div className="stb-co-display mt-0.5 truncate text-lg font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                          {selectedTier.tier}
                          {coupleTicketBadge(selectedTier)
                            ? ` · ${coupleTicketBadge(selectedTier)}`
                            : ""}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                          {personsPerUnit(selectedTier) > 1
                            ? coupleQuantityLabel(qty, selectedTier)
                            : `${formatINR(selectedTier.price)} each`}
                        </div>
                      </div>
                      <div className="inline-flex shrink-0 items-center overflow-hidden rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950">
                        <button
                          type="button"
                          className="flex h-10 w-10 items-center justify-center text-lg font-bold hover:brightness-105"
                          onClick={() => {
                            setQty((q) => {
                              const newQty = Math.max(1, q - 1);
                              clearCouponAndReprice(newQty);
                              return newQty;
                            });
                          }}
                        >
                          −
                        </button>
                        <div className="min-w-[2rem] text-center text-base font-extrabold">
                          {qty}
                        </div>
                        <button
                          type="button"
                          className="flex h-10 w-10 items-center justify-center text-lg font-bold hover:brightness-105 disabled:opacity-50"
                          onClick={() => {
                            setQty((q) => {
                              const newQty = Math.min(maxQtyForTier, q + 1);
                              clearCouponAndReprice(newQty);
                              return newQty;
                            });
                          }}
                          disabled={qty >= maxQtyForTier || maxQtyForTier <= 0}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">
                      No ticket selected. Go back and choose tickets again.
                    </p>
                  )}

                  {/* ✅ Attendee Details — only for Exhibition (ALL required) */}
                  {exhibition && (
                    <div className="mt-2 space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-800">
                      <div className="stb-co-display text-sm font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                        Attendee Details ({qty})
                      </div>
                      {attendees.map((a, idx) => {
                        const nameErr = !isNonEmpty(a.name);
                        const emailErr = !isEmail(a.email);
                        const phoneDigits = String(a.phone || "").replace(/\D/g, "");
                        const phoneErr = !isPhone(phoneDigits);
                        const companyErr = !isNonEmpty(a.companyName);
                        const desigErr = !isNonEmpty(a.designation);
                        return (
                          <div
                            key={idx}
                            className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950"
                          >
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                              Attendee {idx + 1}
                            </div>

                            <div className="grid gap-3 md:grid-cols-3">
                              <input
                                type="text"
                                placeholder="Full Name"
                                value={a.name}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    return next;
                                  })
                                }
                                className={`rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white ${
                                  nameErr
                                    ? "border-red-300 focus:ring-red-300"
                                    : "focus:ring-amber-300 dark:focus:ring-amber-500"
                                }`}
                              />
                              <input
                                type="email"
                                placeholder="Email"
                                value={a.email}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], email: e.target.value };
                                    return next;
                                  })
                                }
                                className={`rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white ${
                                  emailErr
                                    ? "border-red-300 focus:ring-red-300"
                                    : "focus:ring-amber-300 dark:focus:ring-amber-500"
                                }`}
                              />
                              <input
                                type="tel"
                                inputMode="numeric"
                                placeholder="Phone"
                                value={a.phone}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, "");
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], phone: digits };
                                    return next;
                                  });
                                }}
                                className={`rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white ${
                                  phoneErr
                                    ? "border-red-300 focus:ring-red-300"
                                    : "focus:ring-amber-300 dark:focus:ring-amber-500"
                                }`}
                              />
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <input
                                type="text"
                                placeholder="Company Name"
                                value={a.companyName}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = {
                                      ...next[idx],
                                      companyName: e.target.value,
                                    };
                                    return next;
                                  })
                                }
                                className={`rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white ${
                                  companyErr
                                    ? "border-red-300 focus:ring-red-300"
                                    : "focus:ring-amber-300 dark:focus:ring-amber-500"
                                }`}
                              />
                              <input
                                type="text"
                                placeholder="Designation"
                                value={a.designation}
                                onChange={(e) =>
                                  setAttendees((prev) => {
                                    const next = [...prev];
                                    next[idx] = {
                                      ...next[idx],
                                      designation: e.target.value,
                                    };
                                    return next;
                                  })
                                }
                                className={`rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white ${
                                  desigErr
                                    ? "border-red-300 focus:ring-red-300"
                                    : "focus:ring-amber-300 dark:focus:ring-amber-500"
                                }`}
                              />
                            </div>

                            {(nameErr ||
                              emailErr ||
                              phoneErr ||
                              companyErr ||
                              desigErr) && (
                              <p className="text-xs text-red-600">
                                Please enter valid Name, Email, Phone, Company Name and
                                Designation.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* RIGHT: offers + summary */}
          <aside className="order-1 lg:order-2 lg:col-span-1">
            <div className="lg:sticky lg:top-4 space-y-4">
              {/* AVAILABLE OFFERS */}
              <div className="overflow-hidden rounded-2xl border border-amber-500/25 bg-white shadow-sm dark:border-amber-700/30 dark:bg-slate-900">
                <div className="flex items-center justify-between border-b border-amber-500/15 bg-amber-500/10 px-4 py-3">
                  <h3 className="stb-co-display text-sm font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                    Offers
                  </h3>
                  {appliedCoupon && (
                    <button
                      onClick={clearCoupon}
                      className="text-sm font-semibold text-amber-700 hover:underline dark:text-amber-400"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="p-4">
                  {/* Apply input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError("");
                      }}
                      placeholder="Coupon code"
                      className="w-[105px] flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                      disabled={applyingCoupon}
                    />
                    <button
                      onClick={() => applyByCode(couponCode)}
                      className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-extrabold text-slate-950 hover:brightness-105"
                    >
                      Apply
                    </button>

                    {appliedCoupon && (
                      <button
                        onClick={removeCoupon}
                        className="ml-1 rounded-full bg-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {/* T&C link */}
                  <div className="mt-2 relative">
                    <button
                      type="button"
                      onClick={() => setShowPopup(true)}
                      className="text-xs underline hover:text-slate-800 dark:hover:text-white text-slate-600 dark:text-slate-400"
                    >
                      Terms &amp; Conditions
                    </button>

                    {showPopup && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 max-w-md w-[90%] p-5 rounded-xl shadow-lg relative">
                          <h2 className="text-lg font-semibold mb-2 text-amber-700 dark:text-amber-500">
                            Terms & Conditions
                          </h2>
                          <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                            {hardcodedTnc}
                          </pre>
                          <button
                            onClick={() => setShowPopup(false)}
                            className="absolute top-2 right-3 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white text-lg"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {couponError && (
                    <p className="mt-2 text-sm text-red-650">{couponError}</p>
                  )}

                  {/* Coupon list */}
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    {couponsToRender
                      .filter((c) => c.isShow)
                      .map((c) => {
                        const notOkReason = reasonNotEligible(c);
                        const disabled = !!notOkReason;
                        const isApplied = appliedCoupon?.code === c.code;

                        return (
                          <div
                            key={c._id}
                            className={`rounded-lg border dark:border-slate-800 p-3 bg-white dark:bg-slate-900 ${isApplied ? "ring-2 ring-emerald-300 dark:ring-emerald-900/50 border-emerald-400 dark:border-emerald-500" : ""
                              } ${disabled ? "opacity-60" : ""}`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="text-xs font-mono px-2 py-1 rounded bg-slate-900 dark:bg-slate-950 text-white">
                                {c.code}
                              </div>

                              <div className="min-w-0 w-[160px]">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                  {c.discountType === "percentage"
                                    ? `${Number(c.value || 0)}% OFF`
                                    : `₹${Number(c.value || 0)} OFF`}
                                </div>

                                {c.description && (
                                  <div className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                                    {c.description}
                                  </div>
                                )}

                                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                  Min ₹{Number(c.minPurchaseAmount || 0)} • Valid{" "}
                                  {c.validFrom ? format(new Date(c.validFrom), "dd MMM") : "—"} –{" "}
                                  {c.validTo ? format(new Date(c.validTo), "dd MMM") : "—"}
                                </div>

                                {c.termsAndConditions && (
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      onClick={() => openTncFor(c, c.code ? `Code: ${c.code}` : "")}
                                      className="text-[11px] underline text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white"
                                    >
                                      Terms &amp; Conditions
                                    </button>
                                  </div>
                                )}
                              </div>

                              <button
                                disabled={disabled}
                                onClick={() => applyByCode(c.code)}
                                className={`ml-auto rounded-full px-3 py-1.5 text-xs font-semibold ${disabled
                                  ? "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                                  : "bg-emerald-500 dark:bg-emerald-600 text-white hover:bg-emerald-400 dark:hover:bg-emerald-500"
                                  }`}
                              >
                                {isApplied ? "Applied ✓" : "Apply"}
                              </button>
                            </div>

                            {disabled && (
                              <div className="mt-2 text-[11px] text-red-650">
                                {notOkReason}
                              </div>
                            )}
                          </div>
                        );
                      })}

                    {orderedCoupons.length === 0 && (
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        No offers available for this event right now.
                      </div>
                    )}

                    {orderedCoupons.length > SHOW_COUNT && (
                      <div className="mt-3 flex">
                        <button
                          type="button"
                          onClick={() => setShowAllCoupons((v) => !v)}
                          className="ml-auto text-md font-semibold text-amber-700 dark:text-amber-500 hover:underline"
                        >
                          {showAllCoupons ? "Show less" : `View all (${orderedCoupons.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ORDER SUMMARY */}
              <div className="overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-[0_16px_40px_-24px_rgba(180,83,9,0.35)] dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-amber-100 bg-amber-50/80 px-4 py-4 dark:border-slate-800 dark:bg-amber-950/20">
                  <div className="stb-co-display text-[10px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">
                    Order summary
                  </div>
                  <div className="stb-co-display mt-1 text-lg font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                    {selectedTier.tier} × {qty}
                  </div>
                  {personsPerUnit(selectedTier) > 1 ? (
                    <p className="mt-1 text-xs font-semibold text-amber-800 dark:text-amber-300">
                      Valid for {totalPersons(qty, selectedTier)} persons
                    </p>
                  ) : null}
                  {exhibition && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                      Exhibition: maximum 2 Tickets per order.
                    </p>
                  )}
                  {exhibition && !allAttendeesValid && (
                    <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                      Please fill valid details for all attendees.
                    </p>
                  )}
                </div>
                <div className="space-y-2.5 px-4 py-4 text-sm">
                  <RowDark
                    label={
                      personsPerUnit(selectedTier) > 1
                        ? `${isGroupTicket(selectedTier) ? "Group packs" : "Couple tickets"} (${qty} × ${formatINR(
                            priceDetails?.adjustedTicketCategories?.[0]?.price ?? selectedTier.price
                          )})`
                        : `Tickets (${qty} × ${formatINR(
                            priceDetails?.adjustedTicketCategories?.[0]?.price ?? selectedTier.price
                          )})`
                    }
                    value={formatINR(priceDetails?.cartAmount ?? selectedTier.price * qty)}
                  />
                  <RowDark
                    label="Convenience Fees"
                    value={formatINR(priceDetails?.convenienceFee ?? 0)}
                  />
                  {priceDetails?.discountApplied && appliedCoupon?.code && (
                    <RowDark
                      label={`Discount (${appliedCoupon?.code})`}
                      value={`− ${formatINR(priceDetails?.discountAmount ?? 0)}`}
                    />
                  )}
                  <RowDark
                    label={`GST (${((priceDetails?.gstRate ?? 0) * 100) || 0}%)`}
                    value={formatINR(priceDetails?.gstAmount ?? 0)}
                  />
                  <div className="my-2 h-px bg-amber-100 dark:bg-slate-800" />
                  <div className="flex items-center justify-between">
                    <div className="stb-co-display text-sm font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-400">
                      Total payable
                    </div>
                    <div className="stb-co-display text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
                      {formatINR(priceDetails?.finalAmount ?? 0)}
                    </div>
                  </div>
                </div>
                <div className="hidden px-4 pb-4 md:block">
                  <button
                    onClick={submitDetailsAndPay}
                    className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-3.5 font-extrabold text-slate-950 hover:brightness-105 disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400"
                    disabled={bookDisabled}
                  >
                    {payoutLoading || detailsSubmitting
                      ? "Processing..."
                      : "Confirm & Proceed →"}
                  </button>
                  {qty > (selectedTier?.availableSeats ?? 0) && (
                    <p className="mt-2 text-xs text-rose-600">
                      Quantity exceeds available seats.
                    </p>
                  )}
                </div>
              </div>

              <Link
                to={`/book/${event._id}`}
                className="block text-center text-sm font-semibold text-amber-800 hover:underline dark:text-amber-400"
              >
                ← Change tickets
              </Link>
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile checkout footer dock */}
      {selectedTier && (
        <>
          <div aria-hidden className="h-24 md:hidden" />
          <div className="fixed inset-x-0 bottom-[4.75rem] z-40 px-3 md:hidden">
            <div className="mx-auto flex max-w-6xl items-center gap-3 rounded-2xl border border-amber-200 bg-white p-3 shadow-[0_16px_40px_-20px_rgba(180,83,9,0.35)] dark:border-slate-700 dark:bg-slate-900">
              <div className="min-w-0 flex-1 pl-1">
                <div className="stb-co-display text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400">
                  {selectedTier.tier} × {qty}
                </div>
                <div className="stb-co-display truncate text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
                  {formatINR(priceDetails?.finalAmount ?? selectedTier.price * qty)}
                </div>
              </div>
              <button
                type="button"
                onClick={submitDetailsAndPay}
                className="shrink-0 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3 text-sm font-extrabold text-slate-950 disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400"
                disabled={bookDisabled}
              >
                {payoutLoading || detailsSubmitting ? "..." : "Pay →"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Coupon Applied Animated Popup */}
      {/* {showCouponPopup && (
        <div className="fixed inset-0 z-60 flex items-end md:items-center justify-center pointer-events-none">
          <div
            className={`w-full max-w-sm mx-4 mb-8 md:mb-0 transition-all duration-300 transform ${showCouponPopup ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-95"
              } pointer-events-auto`}
          >
            <div
              className="bg-white border rounded-2xl shadow-lg p-4 flex items-start gap-3"
              role="dialog"
              aria-live="polite"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 grid place-items-center rounded-full bg-emerald-100 text-emerald-700 font-bold">
                  ✓
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Coupon applied {couponPopupInfo.code ? `• ${couponPopupInfo.code}` : ""}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      You saved <span className="font-semibold">{formatINR(Number(couponPopupInfo.discountAmount || 0))}</span>
                      {couponPopupInfo.finalAmount ? (
                        <span className="ml-2">• New total {formatINR(Number(couponPopupInfo.finalAmount || 0))}</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="ml-3 flex-shrink-0">
                    <button
                      onClick={() => setShowCouponPopup(false)}
                      className="rounded-full border px-2 py-1 text-xs hover:bg-slate-50"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-slate-500">
                  Discount applied to cart. You can continue to payment.
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* === Mobile-first Coupon Popup (replace previous showCouponPopup block) === */}
      {showCouponPopup && (
        <>
          <style>{`
      /* backdrop */
      .cp-backdrop {
        animation: cp-fade-in .18s ease forwards;
      }
      @keyframes cp-fade-in { from { opacity: 0 } to { opacity: 0.2 } }

      /* card pop (mobile-friendly) */
      .cp-card {
        animation: cp-pop .45s cubic-bezier(.2,.9,.2,1) forwards;
        width: 94vw;
        max-width: 520px;
        border-radius: 16px;
      }
      @keyframes cp-pop {
        0% { opacity: 0; transform: translateY(16px) scale(.98); }
        60% { opacity: 1; transform: translateY(-6px) scale(1.02); }
        100% { transform: translateY(0) scale(1); }
      }

      /* small burst behind icon (kept subtle for mobile) */
      .cp-burst {
        position: absolute;
        width: 120px;
        height: 120px;
        border-radius: 50%;
        filter: blur(12px);
        opacity: 0;
        transform: scale(.7);
        animation: cp-burst .5s cubic-bezier(.2,.9,.2,1) forwards;
        pointer-events: none;
      }
      @keyframes cp-burst {
        0% { opacity: 0; transform: scale(.7) }
        45% { opacity: .9; transform: scale(1.08) }
        100% { opacity: 0; transform: scale(1.3) }
      }

      .cp-icon { animation: cp-icon-pop .42s cubic-bezier(.2,.9,.2,1) forwards; }
      @keyframes cp-icon-pop {
        0% { transform: scale(.6); opacity: 0; }
        60% { transform: scale(1.1); opacity: 1; }
        100% { transform: scale(1); }
      }

      /* confetti container - limited height so mobile doesn't overflow */
      .confetti-zone {
        position: absolute;
        left: 6%;
        right: 6%;
        top: 6%;
        height: 34vh;
        pointer-events: none;
        overflow: visible;
      }
      .confetti {
        position: absolute;
        width: 8px;
        height: 12px;
        border-radius: 2px;
        opacity: 0.95;
        transform-origin: center;
        animation: conf-fall linear forwards;
      }
      @keyframes conf-fall {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(120vh) rotate(540deg); opacity: 0; }
      }

      .cp-amount { animation: cp-pulse 1s ease 0s 2; }
      @keyframes cp-pulse {
        0% { transform: scale(1) }
        50% { transform: scale(1.05) }
        100% { transform: scale(1) }
      }

      /* responsive tweaks */
      @media (min-width: 640px) {
        .cp-card { width: 80vw; }
        .confetti-zone { height: 28vh; left: 20%; right: 20%; }
      }
    `}</style>

          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 cp-backdrop"
            style={{ background: "rgba(0,0,0,0.36)", pointerEvents: "auto" }}
            onClick={() => setShowCouponPopup(false)}
          />

          {/* Confetti limited zone */}
          <div className="fixed inset-0 z-55 pointer-events-none">
            <div className="confetti-zone">
              {[
                { left: "8%", delay: 0, bg: "#f59e0b", dur: 1200 },
                { left: "22%", delay: 80, bg: "#10b981", dur: 1350 },
                { left: "36%", delay: 160, bg: "#06b6d4", dur: 1100 },
                { left: "50%", delay: 40, bg: "#ef4444", dur: 1400 },
                { left: "64%", delay: 100, bg: "#f97316", dur: 1250 },
                { left: "78%", delay: 60, bg: "#60a5fa", dur: 1500 },
              ].map((c, i) => (
                <div
                  key={i}
                  className="confetti"
                  style={{
                    left: c.left,
                    top: `${-6 - (i % 3) * 4}%`,
                    background: c.bg,
                    animationDuration: `${c.dur}ms`,
                    animationDelay: `${c.delay}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Centered card (mobile-friendly stacked layout) */}
          <div
            className="fixed inset-0 z-60 flex items-center justify-center pointer-events-none px-4"
            aria-live="assertive"
          >
            <div className="pointer-events-auto">
              <div className="relative cp-card bg-white dark:bg-slate-900 border dark:border-slate-800 shadow-2xl p-4 md:p-6">
                {/* subtle burst behind icon */}
                <div
                  className="cp-burst"
                  style={{
                    left: "14px",
                    top: "-36px",
                    background: "radial-gradient(circle at 30% 30%, rgba(34,197,94,0.28), rgba(59,130,246,0.08) 45%)",
                  }}
                />

                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="relative">
                    <div className="cp-icon h-16 w-16 md:h-20 md:w-20 rounded-full bg-emerald-50 dark:bg-emerald-950/40 grid place-items-center">
                      <div className="text-2xl md:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400">✓</div>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="text-sm md:text-base font-semibold text-emerald-700 dark:text-emerald-500">
                      Coupon applied {couponPopupInfo.code ? `• ${couponPopupInfo.code}` : ""}
                    </div>

                    <div className="mt-2">
                      <div className="text-xl md:text-2xl font-extrabold text-slate-900 dark:text-white cp-amount">
                        {couponPopupInfo.discountAmount
                          ? `You saved ${formatINR(Number(couponPopupInfo.discountAmount || 0))}`
                          : "Discount applied"}
                      </div>

                      {couponPopupInfo.finalAmount ? (
                        <div className="mt-1 text-sm md:text-base text-slate-650 dark:text-slate-400">
                          New total: <span className="font-semibold">{formatINR(Number(couponPopupInfo.finalAmount || 0))}</span>
                          <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">({couponPopupInfo.cartAmount ? `was ${formatINR(Number(couponPopupInfo.cartAmount || 0))}` : ""})</span>
                        </div>
                      ) : null}

                      <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                        Discount applied to your cart. Proceed to payment to complete booking.
                      </div>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <button
                      onClick={() => {
                        setShowCouponPopup(false);
                      }}
                      className="w-full rounded-lg border dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 px-4 py-3 text-sm"
                    >
                      Close
                    </button>

                    <button
                      onClick={() => {
                        setShowCouponPopup(false);
                        submitDetailsAndPay();
                      }}
                      className="w-full rounded-lg bg-amber-500 dark:bg-amber-600 px-4 py-3 text-sm font-semibold text-white dark:text-slate-950 hover:bg-amber-600 dark:hover:bg-amber-500"
                    >
                      Proceed to payment
                    </button>
                  </div>

                  {/* lightweight breakdown */}
                  <div className="mt-2 w-full text-xs text-slate-600 dark:text-slate-400 grid grid-cols-2 gap-1">
                    <div>Cart</div>
                    <div className="text-right">{formatINR(Number(couponPopupInfo.cartAmount || priceDetails?.cartAmount || 0))}</div>
                    <div>Discount</div>
                    <div className="text-right">− {formatINR(Number(couponPopupInfo.discountAmount || priceDetails?.discountAmount || 0))}</div>
                    <div className="font-semibold text-slate-900 dark:text-white">Payable</div>
                    <div className="text-right font-semibold text-slate-900 dark:text-white">{formatINR(Number(couponPopupInfo.finalAmount || priceDetails?.finalAmount || 0))}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* T&C Modal */}
      {tncOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setTncOpen(false)} />
          <div className="relative z-10 w-[90vw] max-w-xl max-h-[75vh] overflow-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border dark:border-slate-800">
            <div className="p-4 border-b dark:border-slate-800 flex items-center justify-between">
              <h4 className="font-semibold text-slate-900 dark:text-white text-base">{tncTitle}</h4>
              <button
                onClick={() => setTncOpen(false)}
                className="rounded-full border dark:border-slate-700 text-slate-700 dark:text-slate-200 px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                ✕
              </button>
            </div>
            <div className="p-4 text-sm text-slate-700 dark:text-slate-300 leading-6">
              {Array.isArray(tncContent) ? (
                <ul className="list-disc pl-5 space-y-1">
                  {tncContent.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              ) : /<[^>]+>/.test(String(tncContent || "")) ? (
                <div dangerouslySetInnerHTML={{ __html: tncContent }} />
              ) : (
                <pre className="whitespace-pre-wrap">{String(tncContent || "")}</pre>
              )}
            </div>
            <div className="p-4 pt-0">
              <button
                onClick={() => setTncOpen(false)}
                className="ml-auto block rounded-full bg-amber-400 dark:bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-900 dark:text-slate-950 hover:bg-amber-300 dark:hover:bg-amber-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seating Layout Modal */}
      {layoutOpen && layoutSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setLayoutOpen(false)} />
          <div
            className="relative z-10 w-[92vw] max-w-5xl max-h-[80vh] overflow-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border dark:border-slate-800 p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-slate-900 dark:text-white text-base">
                {event?.name ? `${event.name} — Seating layout` : "Seating layout"}
              </h4>
              <button
                onClick={() => setLayoutOpen(false)}
                className="rounded-full border dark:border-slate-700 text-slate-700 dark:text-slate-200 px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                ✕
              </button>
            </div>
            <div className="overflow-auto">
              <img
                src={layoutSrc}
                alt="Seating layout"
                className="w-full h-auto rounded-lg border dark:border-slate-800"
                loading="lazy"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Layout is indicative and may vary on the day of the event.
            </p>
          </div>
        </div>
      )}

      {showPopupSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-2xl shadow-xl max-w-lg w-full p-6 text-center animate-fadeIn">
            <div className="flex flex-col items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>

              <h2 className="text-xl font-semibold text-gray-805 dark:text-white">Booking Completed!</h2>
              <p className="text-gray-600 dark:text-gray-350 text-sm">
                Your booking has been successfully completed. You can view your Tickets in “My Tickets”.
              </p>

              <div className="mt-3 w-full bg-gray-50 dark:bg-slate-950 border dark:border-slate-800 rounded-lg p-3 text-left text-sm">
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Booking Code:</span>
                  <span className="text-gray-900 dark:text-white font-semibold">
                    {popupBooking?.bookingCode || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                  <span className={`font-semibold ${popupBooking?.bookingStatus === "Confirmed" ? "text-green-600 dark:text-green-550" : "text-orange-600 dark:text-orange-550"}`}>
                    {popupBooking?.bookingStatus || "Pending"}
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowPopupSuccess(false);
                  nav("/tickets");
                }}
                className="mt-5 bg-amber-600 dark:bg-amber-500 text-white dark:text-slate-950 px-6 py-2 rounded-md hover:bg-amber-700 dark:hover:bg-amber-400 transition"
              >
                View My Tickets
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Helpers */
function RowDark({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-slate-500 dark:text-slate-400">{label}</div>
      <div className="shrink-0 font-semibold tabular-nums text-slate-900 dark:text-white">{value}</div>
    </div>
  );
}
